import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import SkillIcon from '../general/SkillIcon';
import SkillCheckboxTableCell from './SkillCheckboxTableCell';
import './SkillEditTable.css';
import { operations as membershipOperations, selectors as membershipSelectors } from '../../state/modules/memberships';
import { operations as skillOperations, selectors as skillSelectors } from '../../state/modules/skills';
import { operations as userOperations, selectors as userSelectors } from '../../state/modules/users';
import {
  operations as certificationOperations,
  selectors as certificationSelectors
} from '../../state/modules/certifications';

import Loader from '../general/Loader';
import { useWindowDimensions } from '../../utils/custom-hooks';
import { Table, Dropdown, Modal, Button, Form } from '../ui-kit';
import DateTimePicker from '../general/DateTimePicker';
import moment from 'moment';

const emptyObject = {};
const emptyArray = [];

const certificationsFor = (membership, skill, certificationsByMembershipAndSkillId) =>
  (certificationsByMembershipAndSkillId[membership.id] || emptyObject)[skill.id] || emptyArray;

const ASSIGN = 'assign';
const REVOKE = 'revoke';

const AssignRevokeDialog = ({ type, onCancel, onSubmit, userName, skillName }) => {
  const show = !!type;
  const [time, setTime] = useState(null);
  const [comments, setComments] = useState('');

  // reset time to now whenever the dialog is opened, so that if the user opens
  // the dialog, changes the time, cancels, and opens again, the time will be
  // updated to the current time instead of staying at the previously selected
  useEffect(() => {
    if (!show) return;
    setTime(moment().startOf('day').format());
    setComments('');
  }, [show]);

  if (!show) return null;

  const label =
    type === REVOKE
      ? I18n.t('activerecord.attributes.certification.end_time')
      : I18n.t('activerecord.attributes.certification.start_time');

  const titleI18nKey = type === REVOKE ? 'group_skills.dialog.title.revoke' : 'group_skills.dialog.title.assign';
  const title = I18n.t(titleI18nKey, { skill_name: skillName, user_name: userName });

  return (
    <Modal show={show} onHide={onCancel}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="startTime">
            <Form.Label column sm={3}>
              {label}
            </Form.Label>
            <DateTimePicker name="startTime" value={time} onChange={setTime} customLabel={label} />
          </Form.Group>
        </Form>
        <Form.Group controlId="comments" className="mt-3">
          <Form.Label column sm={3}>
            {I18n.t('comments')}
          </Form.Label>
          <Form.Control as="textarea" rows={3} value={comments} onChange={e => setComments(e.target.value)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>
          {I18n.t('cancel')}
        </Button>
        <Button variant={type == REVOKE ? 'danger' : 'primary'} onClick={() => onSubmit(time, comments)}>
          {I18n.t('save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ContextMenu = ({ show, xCoord, yCoord, showDialog }) => {
  return (
    <Dropdown.ContextMenu x={xCoord} y={yCoord} show={show}>
      <Dropdown.Item onClick={() => showDialog(ASSIGN)}>
        {I18n.t('group_skills.context_menu.assign_skill_at')}
      </Dropdown.Item>
      <Dropdown.Item onClick={() => showDialog(REVOKE)}>
        {I18n.t('group_skills.context_menu.revoke_skill_at')}
      </Dropdown.Item>
    </Dropdown.ContextMenu>
  );
};

const SkillEditTable = ({
  groupId,
  fetchSkills,
  fetchMemberships,
  fetchUsers,
  fetchCertifications,
  loaded,
  skills,
  memberships,
  membershipIds,
  certificationsByMembershipAndSkillId,
  assignSkill,
  userId
}) => {
  const [showContextMenu, setShowContextMenu] = useState({
    xCoord: null,
    yCoord: null,
    show: false,
    skillId: null,
    membershipId: null
  });
  const [showDialogType, setShowDialogType] = useState(null);

  const displayContextMenu = useCallback((event, membershipId, skillId) => {
    event.preventDefault();
    setShowContextMenu({
      xCoord: event.pageX,
      yCoord: event.pageY,
      show: true,
      skillId,
      membershipId
    });
  }, []);

  const showDialog = useCallback(type => {
    setShowDialogType(type);
    setShowContextMenu(prev => ({ ...prev, show: false }));
  }, []);

  const dialogSubmit = useCallback(
    (time, comments) => {
      const { membershipId, skillId } = showContextMenu;
      const possessed = showDialogType === ASSIGN;
      assignSkill({ membershipId, skillId, possessed, time, comments });
      setShowDialogType(null);
    },
    [assignSkill, showContextMenu, showDialogType]
  );

  const closeContextMenu = useCallback(() => {
    setShowContextMenu(prev => ({ ...prev, show: false }));
  }, []);

  useEffect(() => {
    document.addEventListener('click', closeContextMenu);
    return () => {
      document.removeEventListener('click', closeContextMenu);
    };
  }, [closeContextMenu]);

  useEffect(() => {
    fetchSkills({ params: { group_ids: groupId } });
    fetchMemberships({
      params: { ids: membershipIds, presentation_type: 'basic' },
      forceFetch: true
    });
    // If an userId is provided as a prop, the component will be in
    // single user mode, and only need to fetch that user's data
    if (userId) {
      fetchUsers({ params: { ids: userId, presentation_type: 'basic' } });
    } else {
      fetchUsers({ params: { group_ids: groupId, presentation_type: 'basic' } });
    }
    fetchCertifications({ params: { group_ids: groupId } });
  }, []);

  const { height } = useWindowDimensions();

  if (!loaded) return <Loader />;

  const membershipsToShow = memberships.filter(membership => membershipIds.includes(membership.id));
  return (
    <>
      <ContextMenu
        xCoord={showContextMenu.xCoord}
        yCoord={showContextMenu.yCoord}
        show={showContextMenu.show}
        showDialog={showDialog}
      />
      <AssignRevokeDialog
        userName={memberships.find(m => m.id === showContextMenu.membershipId)?.user?.full_name}
        skillName={skills.find(s => s.id === showContextMenu.skillId)?.name}
        type={showDialogType}
        onCancel={() => setShowDialogType(null)}
        onSubmit={dialogSubmit}
      />
      <div className="overflow-y-scroll" style={{ maxHeight: height * 0.67 }}>
        <Table hover>
          <thead>
            <tr>
              <th className="position-sticky top-0 start-0 z-3" style={{ minWidth: '250px' }}>
                {I18n.t('activerecord.models.user')}
              </th>
              {skills.map(skill => (
                <th
                  key={skill.id}
                  align="center"
                  className="position-sticky top-0 z-2"
                  style={{ minWidth: '75px', textAlign: 'center' }}
                >
                  <SkillIcon skill={skill} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {membershipsToShow
              .filter(m => m.user)
              .map(membership => (
                <tr key={membership.id}>
                  <td className="position-sticky start-0 z-1">
                    <a href={`/users/${membership.user_id}`} target="_blank" rel="noreferrer">
                      {membership.user?.full_name}
                    </a>
                  </td>
                  {skills.map(skill => (
                    <td align="center" key={`membership_${membership.id}_skill_${skill.id}`}>
                      <SkillCheckboxTableCell
                        onContextMenu={displayContextMenu}
                        membershipId={membership.id}
                        skillId={skill.id}
                        certifications={certificationsFor(membership, skill, certificationsByMembershipAndSkillId)}
                        skillName={skill.name}
                        userName={membership.user?.full_name}
                        assignSkill={assignSkill}
                      />
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  skills: skillSelectors.values(state),
  memberships: Object.values(membershipSelectors.membershipsWithUser(state)).sort(
    (a, b) => a.display_order - b.display_order
  ),
  loaded:
    userSelectors.initialLoadComplete(state) &&
    membershipSelectors.initialLoadComplete(state) &&
    skillSelectors.initialLoadComplete(state),
  certificationsByMembershipAndSkillId: certificationSelectors.certificationsByMembershipAndSkillId(state)
});

const mapDispatchToProps = {
  fetchSkills: skillOperations.fetch,
  fetchMemberships: membershipOperations.fetch,
  fetchUsers: userOperations.fetch,
  fetchCertifications: certificationOperations.fetch,
  assignSkill: certificationOperations.assignSkill
};

export default connect(mapStateToProps, mapDispatchToProps)(SkillEditTable);
